import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';
import { CreditCard, Plus, Search, X, Check, Loader, AlertCircle } from 'lucide-react';

const GITHUB_ASSETS_API_BASE = 'https://api.github.com/repos/TokenRevolution/dios10_assets/contents';
const GITHUB_ASSETS_RAW_BASE = 'https://raw.githubusercontent.com/TokenRevolution/dios10_assets/main';

const normalizeCustomGithubImagePath = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';

  if (trimmed.startsWith('https://github.com/')) {
    return trimmed.replace('https://github.com/', 'https://raw.githubusercontent.com/').replace('/blob/', '/');
  }

  if (trimmed.startsWith('/custom/')) {
    return `${GITHUB_ASSETS_RAW_BASE}${trimmed}`;
  }

  if (trimmed.startsWith('custom/')) {
    return `${GITHUB_ASSETS_RAW_BASE}/${trimmed}`;
  }

  if (trimmed.startsWith('/main/custom/')) {
    return `${GITHUB_ASSETS_RAW_BASE}${trimmed.replace('/main', '')}`;
  }

  if (trimmed.startsWith('main/custom/')) {
    return `${GITHUB_ASSETS_RAW_BASE}/${trimmed.replace(/^main\//, '')}`;
  }

  return trimmed;
};

const getGithubImagePathFromValue = (value) => {
  const normalized = normalizeCustomGithubImagePath(value);
  const prefix = `${GITHUB_ASSETS_RAW_BASE}/`;
  if (normalized.startsWith(prefix)) {
    return normalized.slice(prefix.length);
  }
  return '';
};

const AdminCards = () => {
  const [players, setPlayers] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [rarities, setRarities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [remainingInfo, setRemainingInfo] = useState(null);
  const [remainingLoading, setRemainingLoading] = useState(false);
  const [remainingError, setRemainingError] = useState('');
  
  // Form state
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerSearch, setPlayerSearch] = useState('');
  const [showPlayerDropdown, setShowPlayerDropdown] = useState(false);
  const [selectedRarity, setSelectedRarity] = useState('');
  const [selectedLeague, setSelectedLeague] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [creationMode, setCreationMode] = useState('player'); // player | team | custom
  const [customCardKind, setCustomCardKind] = useState('legend');
  const [customCardName, setCustomCardName] = useState('');
  const [customImagePath, setCustomImagePath] = useState('');
  const [scoreBonusPercent, setScoreBonusPercent] = useState('');
  const [scoreBonusPosition, setScoreBonusPosition] = useState('');
  const [scoreBonusLeague, setScoreBonusLeague] = useState('');
  const [githubCustomImages, setGithubCustomImages] = useState([]);
  const [githubCustomImagesLoading, setGithubCustomImagesLoading] = useState(false);
  const [githubCustomImagesError, setGithubCustomImagesError] = useState('');
  const [selectedGithubImagePath, setSelectedGithubImagePath] = useState('');
  const [availableCopies, setAvailableCopies] = useState('');
  // Team mode: players of selected team and which to include for card generation
  const [teamPlayers, setTeamPlayers] = useState([]);
  const [teamPlayersLoading, setTeamPlayersLoading] = useState(false);
  const [selectedPlayerIdsForBulk, setSelectedPlayerIdsForBulk] = useState(new Set()); // player ids to include (generate card)
  // Sale options
  const [price, setPrice] = useState('');
  const [createSale, setCreateSale] = useState(false);
  // Auction options
  const [createAuction, setCreateAuction] = useState(false);
  const [auctionStartingPrice, setAuctionStartingPrice] = useState('');
  const [auctionMinIncrementPercent, setAuctionMinIncrementPercent] = useState('5');
  const [auctionTimeExtensionSeconds, setAuctionTimeExtensionSeconds] = useState('30');
  const [auctionDurationHours, setAuctionDurationHours] = useState('24');
  const [auctionDurationMinutes, setAuctionDurationMinutes] = useState('0');
  const [auctionDurationSeconds, setAuctionDurationSeconds] = useState('0');
  // Loan options
  const [createLoan, setCreateLoan] = useState(false);
  const [loanPricePerGW, setLoanPricePerGW] = useState('');
  const [loanDurationGW, setLoanDurationGW] = useState('1');

  useEffect(() => {
    loadRarities();
    loadLeagues();
  }, []);

  useEffect(() => {
    if (playerSearch.length >= 2) {
      searchPlayers();
    } else {
      setPlayers([]);
      setShowPlayerDropdown(false);
    }
  }, [playerSearch]);

  useEffect(() => {
    if (selectedLeague) {
      loadTeams(selectedLeague);
    } else {
      setTeams([]);
      setSelectedTeam('');
    }
  }, [selectedLeague]);

  // Load team players when in team mode and league+team are selected
  useEffect(() => {
    if (creationMode === 'team' && selectedLeague && selectedTeam) {
      loadTeamPlayers(selectedLeague, selectedTeam);
    } else {
      setTeamPlayers([]);
      setSelectedPlayerIdsForBulk(new Set());
    }
  }, [creationMode, selectedLeague, selectedTeam]);

  useEffect(() => {
    const currentRarity = rarities.find((rarity) => rarity.id.toString() === selectedRarity);
    if (creationMode === 'custom' && currentRarity && !['plus', 'rare', 'epic', 'unique'].includes(currentRarity.name)) {
      setSelectedRarity('');
    }
  }, [creationMode, selectedRarity, rarities]);

  useEffect(() => {
    if (creationMode === 'player' && selectedPlayer && selectedRarity) {
      loadRemainingCopies();
    } else {
      setRemainingInfo(null);
      setRemainingError('');
    }
  }, [selectedPlayer, selectedRarity, creationMode]);

  useEffect(() => {
    if (creationMode !== 'custom') {
      setGithubCustomImages([]);
      setGithubCustomImagesError('');
      setGithubCustomImagesLoading(false);
      setSelectedGithubImagePath('');
      return;
    }

    const loadGithubCustomImages = async () => {
      try {
        setGithubCustomImagesLoading(true);
        setGithubCustomImagesError('');

        const response = await fetch(`${GITHUB_ASSETS_API_BASE}/custom/${customCardKind}`);
        if (!response.ok) {
          throw new Error(`GitHub responded with ${response.status}`);
        }

        const items = await response.json();
        const imageExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.gif'];
        const images = (Array.isArray(items) ? items : [])
          .filter((item) => item?.type === 'file' && imageExtensions.some((ext) => String(item.name || '').toLowerCase().endsWith(ext)))
          .map((item) => ({
            name: item.name,
            path: item.path,
            rawUrl: item.download_url || `${GITHUB_ASSETS_RAW_BASE}/${item.path}`,
            htmlUrl: item.html_url || ''
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setGithubCustomImages(images);
      } catch (error) {
        console.error(`Error loading GitHub ${customCardKind} images:`, error);
        setGithubCustomImages([]);
        setGithubCustomImagesError(`Failed to load ${customCardKind} images from GitHub`);
      } finally {
        setGithubCustomImagesLoading(false);
      }
    };

    loadGithubCustomImages();
  }, [creationMode, customCardKind]);

  useEffect(() => {
    const matchedGithubPath = getGithubImagePathFromValue(customImagePath);
    const hasMatch = githubCustomImages.some((image) => image.path === matchedGithubPath);
    setSelectedGithubImagePath(hasMatch ? matchedGithubPath : '');
  }, [customImagePath, githubCustomImages]);

  const loadRarities = async () => {
    try {
      const response = await adminApi.getRarities();
      if (response?.success) {
        setRarities(response.rarities || []);
      }
    } catch (error) {
      console.error('Error loading rarities:', error);
      showMessage('error', 'Failed to load rarities');
    }
  };

  const loadLeagues = async () => {
    try {
      const response = await adminApi.getLeagues();
      if (response?.success) {
        setLeagues(response.leagues || []);
      }
    } catch (error) {
      console.error('Error loading leagues:', error);
      showMessage('error', 'Failed to load leagues');
    }
  };

  const loadTeams = async (league) => {
    try {
      const response = await adminApi.getTeamsByLeague(league);
      if (response?.success) {
        setTeams(response.teams || []);
      }
    } catch (error) {
      console.error('Error loading teams:', error);
      showMessage('error', 'Failed to load teams');
    }
  };

  const loadTeamPlayers = async (league, team) => {
    try {
      setTeamPlayersLoading(true);
      const response = await adminApi.getPlayers({
        league,
        team,
        limit: 500
      });
      if (response?.success && response.players?.length) {
        const forTeam = response.players.filter((p) => p.team === team);
        setTeamPlayers(forTeam);
        setSelectedPlayerIdsForBulk(new Set(forTeam.map((p) => p.id)));
      } else {
        setTeamPlayers([]);
        setSelectedPlayerIdsForBulk(new Set());
      }
    } catch (error) {
      console.error('Error loading team players:', error);
      showMessage('error', 'Failed to load team players');
      setTeamPlayers([]);
      setSelectedPlayerIdsForBulk(new Set());
    } finally {
      setTeamPlayersLoading(false);
    }
  };

  const togglePlayerForBulk = (playerId) => {
    setSelectedPlayerIdsForBulk((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) next.delete(playerId);
      else next.add(playerId);
      return next;
    });
  };

  const selectAllTeamPlayers = () => {
    setSelectedPlayerIdsForBulk(new Set(teamPlayers.map((p) => p.id)));
  };

  const deselectAllTeamPlayers = () => {
    setSelectedPlayerIdsForBulk(new Set());
  };

  const searchPlayers = async () => {
    try {
      const response = await adminApi.getPlayers({
        search: playerSearch,
        limit: 20
      });
      if (response?.success) {
        setPlayers(response.players || []);
        setShowPlayerDropdown(true);
      }
    } catch (error) {
      console.error('Error searching players:', error);
      showMessage('error', 'Failed to search players');
    }
  };

  const loadRemainingCopies = async () => {
    try {
      setRemainingLoading(true);
      setRemainingError('');
      const response = await adminApi.getRemainingCopies(selectedPlayer.id, selectedRarity);
      if (response?.success) {
        setRemainingInfo(response.result || null);
      } else {
        setRemainingInfo(null);
        setRemainingError(response?.error || 'Failed to load remaining copies');
      }
    } catch (error) {
      setRemainingInfo(null);
      setRemainingError(error.response?.data?.error || error.message || 'Failed to load remaining copies');
    } finally {
      setRemainingLoading(false);
    }
  };

  const handlePlayerSelect = (player) => {
    setSelectedPlayer(player);
    setPlayerSearch(`${player.fullName} (${player.position}) - ${player.team || 'N/A'}`);
    setShowPlayerDropdown(false);
  };

  const handleGithubImageSelect = (githubPath) => {
    setSelectedGithubImagePath(githubPath);
    if (!githubPath) {
      setCustomImagePath('');
      return;
    }

    const selectedImage = githubCustomImages.find((image) => image.path === githubPath);
    setCustomImagePath(selectedImage ? selectedImage.rawUrl : normalizeCustomGithubImagePath(githubPath));
  };

  const handleCustomImagePathChange = (value) => {
    setCustomImagePath(value);
  };

  const handleCustomImagePathBlur = () => {
    setCustomImagePath((current) => normalizeCustomGithubImagePath(current));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (creationMode === 'player') {
      if (!selectedPlayer) {
        showMessage('error', 'Please select a player');
        return;
      }
    } else if (creationMode === 'team') {
      if (!selectedLeague || !selectedTeam) {
        showMessage('error', 'Please select a league and team');
        return;
      }
      if (selectedPlayerIdsForBulk.size === 0) {
        showMessage('error', 'Seleziona almeno un player per cui generare le card');
        return;
      }
    } else {
      if (!customCardName.trim()) {
        showMessage('error', 'Please enter a custom card name');
        return;
      }
      if (!['legend', 'divinity'].includes(customCardKind)) {
        showMessage('error', 'Custom card type must be Legend or Divinity');
        return;
      }
    }
    
    if (!selectedRarity) {
      showMessage('error', 'Please select a rarity');
      return;
    }

    if (creationMode === 'custom') {
      const allowedCustomRarities = ['plus', 'rare', 'epic', 'unique'];
      if (!allowedCustomRarities.includes(selectedRarityData?.name)) {
        showMessage('error', 'Custom Legend/Divinity cards can only use Plus, Rare, Epic, or Unique rarities');
        return;
      }
    }
    
    if (creationMode === 'player' && remainingInfo && remainingInfo.remainingCopies !== null) {
      const remaining = parseInt(remainingInfo.remainingCopies);
      if (remaining <= 0) {
        showMessage('error', 'No copies available for this player and rarity');
        return;
      }
    }
    
    if (!availableCopies || parseInt(availableCopies) <= 0) {
      showMessage('error', 'Available copies must be greater than 0');
      return;
    }

    if (creationMode === 'player' && remainingInfo && remainingInfo.remainingCopies !== null) {
      const remaining = parseInt(remainingInfo.remainingCopies);
      if (parseInt(availableCopies) > remaining) {
        showMessage('error', `Available copies cannot exceed remaining limit (${remaining})`);
        return;
      }
    }
    
    // Validate marketplace options (only one can be selected)
    const marketplaceOptionsCount = (createSale ? 1 : 0) + (createAuction ? 1 : 0) + (createLoan ? 1 : 0);
    if (marketplaceOptionsCount > 1) {
      showMessage('error', 'Please select only one marketplace option: Sale, Auction, or Loan');
      return;
    }
    
    if (createSale && (!price || parseFloat(price) <= 0)) {
      showMessage('error', 'Price must be greater than 0 when creating sale');
      return;
    }
    
    if (createAuction && (!auctionStartingPrice || parseFloat(auctionStartingPrice) <= 0)) {
      showMessage('error', 'Auction starting price must be greater than 0');
      return;
    }

    if (createAuction) {
      const totalSeconds = (parseInt(auctionDurationHours, 10) * 3600)
        + (parseInt(auctionDurationMinutes, 10) * 60)
        + parseInt(auctionDurationSeconds, 10);
      if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
        showMessage('error', 'Auction duration must be greater than 0 seconds');
        return;
      }
    }
    
    if (createLoan) {
      if (!loanPricePerGW || parseFloat(loanPricePerGW) <= 0) {
        showMessage('error', 'Loan price per gameweek must be greater than 0');
        return;
      }
      if (![1, 3, 5, 10].includes(parseInt(loanDurationGW))) {
        showMessage('error', 'Loan duration must be 1, 3, 5, or 10 gameweeks');
        return;
      }
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const cardData = {
        playerId: creationMode === 'player' ? selectedPlayer?.id : undefined,
        league: selectedLeague,
        team: selectedTeam,
        rarityId: parseInt(selectedRarity),
        totalCopies: parseInt(availableCopies),
        availableCopies: parseInt(availableCopies),
        cardKind: creationMode === 'custom' ? customCardKind : 'player',
        customName: creationMode === 'custom' ? customCardName.trim() : undefined,
        customImagePath: creationMode === 'custom' && customImagePath.trim() ? customImagePath.trim() : undefined,
        scoreBonusPercent: creationMode === 'custom' && scoreBonusPercent !== '' ? parseFloat(scoreBonusPercent) : undefined,
        scoreBonusPosition: creationMode === 'custom' && scoreBonusPosition ? scoreBonusPosition : undefined,
        scoreBonusLeague: creationMode === 'custom' && scoreBonusLeague ? scoreBonusLeague : undefined,
        ...(creationMode === 'team' && selectedPlayerIdsForBulk.size > 0 && {
          playerIds: Array.from(selectedPlayerIdsForBulk)
        }),
        // Sale options
        price: createSale && price ? parseFloat(price) : undefined,
        createSale: createSale && price && parseFloat(price) > 0,
        // Auction options
        createAuction: createAuction && auctionStartingPrice && parseFloat(auctionStartingPrice) > 0,
        auctionStartingPrice: createAuction && auctionStartingPrice ? parseFloat(auctionStartingPrice) : undefined,
        auctionMinIncrementPercent: createAuction ? parseFloat(auctionMinIncrementPercent) : undefined,
        auctionTimeExtensionSeconds: createAuction ? parseInt(auctionTimeExtensionSeconds) : undefined,
        auctionDurationHours: createAuction ? parseInt(auctionDurationHours) : undefined,
        auctionDurationMinutes: createAuction ? parseInt(auctionDurationMinutes) : undefined,
        auctionDurationSeconds: createAuction ? parseInt(auctionDurationSeconds) : undefined,
        // Loan options
        createLoan: createLoan && loanPricePerGW && parseFloat(loanPricePerGW) > 0,
        loanPricePerGW: createLoan && loanPricePerGW ? parseFloat(loanPricePerGW) : undefined,
        loanDurationGW: createLoan ? parseInt(loanDurationGW) : undefined
      };

      const response = creationMode === 'team'
        ? await adminApi.createCardsBulk(cardData)
        : await adminApi.createCard(cardData);
      
      if (response?.success) {
        if (creationMode === 'player') {
          const card = response.card;
          let successMsg = `Card created successfully! Template ID: ${card.templateId}, Created ${card.createdCardIds.length} card instances`;
          if (card.cardsTable && card.templateCardCount !== undefined) {
            successMsg += ` (table: ${card.cardsTable}, total in template: ${card.templateCardCount})`;
          }
          if (card.createdSales && card.createdSales.length > 0) {
            successMsg += `, ${card.createdSales.length} sales created`;
          }
          if (card.createdAuctions && card.createdAuctions.length > 0) {
            successMsg += `, ${card.createdAuctions.length} auctions created`;
          }
          if (card.createdLoans && card.createdLoans.length > 0) {
            successMsg += `, ${card.createdLoans.length} loans created`;
          }
          showMessage('success', successMsg);
        } else if (creationMode === 'custom') {
          const card = response.card;
          let successMsg = `${customCardKind === 'legend' ? 'Legend' : 'Divinity'} card created successfully: ${customCardName.trim()}. Created ${card.createdCardIds.length} card instances`;
          if (card.cardsTable && card.templateCardCount !== undefined) {
            successMsg += ` (table: ${card.cardsTable}, total in template: ${card.templateCardCount})`;
          }
          if (card.createdSales && card.createdSales.length > 0) {
            successMsg += `, ${card.createdSales.length} sales created`;
          }
          if (card.createdAuctions && card.createdAuctions.length > 0) {
            successMsg += `, ${card.createdAuctions.length} auctions created`;
          }
          if (card.createdLoans && card.createdLoans.length > 0) {
            successMsg += `, ${card.createdLoans.length} loans created`;
          }
          showMessage('success', successMsg);
        } else {
          const result = response.result;
          let successMsg = `Bulk creation completed. Players: ${result.totalPlayers}, Created: ${result.created}, Failed: ${result.failed}`;
          if (result.failures && result.failures.length > 0) {
            const firstFailure = result.failures[0];
            successMsg += ` (first error: player ${firstFailure.playerId} - ${firstFailure.error})`;
          }
          showMessage('success', successMsg);
        }
        resetFormAfterCreation();
      } else {
        showMessage('error', response?.error || 'Failed to create card');
      }
    } catch (error) {
      console.error('Error creating card:', error);
      showMessage('error', error.response?.data?.error || error.message || 'Failed to create card');
    } finally {
      setLoading(false);
    }
  };

  /** Reset only card-specific data after successful creation; keep filters (creationMode, league, team, rarity, team players selection, customCardKind) */
  const resetFormAfterCreation = () => {
    setSelectedPlayer(null);
    setPlayerSearch('');
    setAvailableCopies('');
    setCustomCardName('');
    setCustomImagePath('');
    setSelectedGithubImagePath('');
    setPrice('');
    setCreateSale(false);
    setCreateAuction(false);
    setAuctionStartingPrice('');
    setAuctionMinIncrementPercent('5');
    setAuctionTimeExtensionSeconds('30');
    setAuctionDurationHours('24');
    setAuctionDurationMinutes('0');
    setAuctionDurationSeconds('0');
    setCreateLoan(false);
    setLoanPricePerGW('');
    setLoanDurationGW('1');
    setRemainingInfo(null);
    setRemainingError('');
  };

  const resetForm = () => {
    setSelectedPlayer(null);
    setPlayerSearch('');
    setSelectedRarity('');
    setSelectedLeague('');
    setSelectedTeam('');
    setCustomCardKind('legend');
    setScoreBonusPercent('');
    setScoreBonusPosition('');
    setScoreBonusLeague('');
    setCustomCardName('');
    setCustomImagePath('');
    setGithubCustomImages([]);
    setGithubCustomImagesError('');
    setSelectedGithubImagePath('');
    setAvailableCopies('');
    setTeamPlayers([]);
    setSelectedPlayerIdsForBulk(new Set());
    // Sale
    setPrice('');
    setCreateSale(false);
    // Auction
    setCreateAuction(false);
    setAuctionStartingPrice('');
    setAuctionMinIncrementPercent('5');
    setAuctionTimeExtensionSeconds('30');
    setAuctionDurationHours('24');
    setAuctionDurationMinutes('0');
    // Loan
    setCreateLoan(false);
    setLoanPricePerGW('');
    setLoanDurationGW('1');
    setRemainingInfo(null);
    setRemainingError('');
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const selectedRarityData = rarities.find(r => r.id.toString() === selectedRarity);
  const visibleRarities = creationMode === 'custom'
    ? rarities.filter((rarity) => ['plus', 'rare', 'epic', 'unique'].includes(rarity.name))
    : rarities;
  const remainingMax = remainingInfo && remainingInfo.remainingCopies !== null
    ? parseInt(remainingInfo.remainingCopies)
    : undefined;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold flex items-center">
          <CreditCard className="w-8 h-8 mr-3 text-indigo-400" />
          Cards Management
        </h1>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-900/50 border-green-700 text-green-200' 
            : 'bg-red-900/50 border-red-700 text-red-200'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <Check className="w-5 h-5 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-2" />
            )}
            <span>{message.text}</span>
          </div>
        </div>
      )}

      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h2 className="text-xl font-semibold mb-6 flex items-center">
          <Plus className="w-5 h-5 mr-2 text-indigo-400" />
          Create New Card
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Creation Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Creation Mode
            </label>
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2 text-sm text-gray-300">
                <input
                  type="radio"
                  name="creationMode"
                  value="player"
                  checked={creationMode === 'player'}
                  onChange={() => setCreationMode('player')}
                />
                <span>Single Player</span>
              </label>
              <label className="flex items-center space-x-2 text-sm text-gray-300">
                <input
                  type="radio"
                  name="creationMode"
                  value="team"
                  checked={creationMode === 'team'}
                  onChange={() => setCreationMode('team')}
                />
                <span>By Team (league + team)</span>
              </label>
              <label className="flex items-center space-x-2 text-sm text-gray-300">
                <input
                  type="radio"
                  name="creationMode"
                  value="custom"
                  checked={creationMode === 'custom'}
                  onChange={() => setCreationMode('custom')}
                />
                <span>Custom Legend/Divinity</span>
              </label>
            </div>
          </div>

          {creationMode === 'team' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  League <span className="text-red-400">*</span>
                </label>
                <select
                  value={selectedLeague}
                  onChange={(e) => setSelectedLeague(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required={creationMode === 'team'}
                >
                  <option value="">Select a league...</option>
                  {leagues.map((league) => (
                    <option key={league} value={league}>{league}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Team <span className="text-red-400">*</span>
                </label>
                <select
                  value={selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required={creationMode === 'team'}
                  disabled={!selectedLeague}
                >
                  <option value="">Select a team...</option>
                  {teams.map((team) => (
                    <option key={team} value={team}>{team}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {creationMode === 'custom' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Card Type <span className="text-red-400">*</span>
                </label>
                <select
                  value={customCardKind}
                  onChange={(e) => setCustomCardKind(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required={creationMode === 'custom'}
                >
                  <option value="legend">Legend</option>
                  <option value="divinity">Divinity</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Card Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={customCardName}
                  onChange={(e) => setCustomCardName(e.target.value)}
                  placeholder="Enter custom card name"
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required={creationMode === 'custom'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Score Bonus %
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={scoreBonusPercent}
                  onChange={(e) => setScoreBonusPercent(e.target.value)}
                  placeholder="es. 5"
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <p className="mt-1 text-xs text-gray-400">Bonus % score sul ruolo e/o la lega selezionati (es. 5 = +5%)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Ruolo potenziato
                </label>
                <select
                  value={scoreBonusPosition}
                  onChange={(e) => setScoreBonusPosition(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Nessuno</option>
                  <option value="GK">Portieri (GK)</option>
                  <option value="DEF">Difensori (DEF)</option>
                  <option value="MID">Centrocampisti (MID)</option>
                  <option value="ATT">Attaccanti (ATT)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Lega potenziata
                </label>
                <select
                  value={scoreBonusLeague}
                  onChange={(e) => setScoreBonusLeague(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Nessuna</option>
                  {leagues.map((league) => (
                    <option key={league} value={league}>{league}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-400">es. Serie A, La Liga, Premier League</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  GitHub Image
                </label>
                <select
                  value={selectedGithubImagePath}
                  onChange={(e) => handleGithubImageSelect(e.target.value)}
                  className="w-full px-4 py-2 mb-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                  disabled={githubCustomImagesLoading}
                >
                  <option value="">
                    {githubCustomImagesLoading
                      ? `Loading ${customCardKind} images from GitHub...`
                      : `Select a ${customCardKind} image from GitHub...`}
                  </option>
                  {githubCustomImages.map((image) => (
                    <option key={image.path} value={image.path}>
                      {image.name}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={customImagePath}
                  onChange={(e) => handleCustomImagePathChange(e.target.value)}
                  onBlur={handleCustomImagePathBlur}
                  placeholder="Paste a GitHub blob/raw URL or a custom/legend|divinity path"
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {githubCustomImagesError && (
                  <p className="mt-1 text-xs text-red-300">
                    {githubCustomImagesError}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-400">
                  You can select an image from `TokenRevolution/dios10_assets/custom/{customCardKind}` or paste a GitHub URL. `github.com/.../blob/...` links are converted automatically to `raw.githubusercontent.com`.
                </p>
                {customImagePath && (
                  <div className="mt-3 flex items-start gap-4 rounded-lg border border-gray-700 bg-gray-900/50 p-3">
                    <img
                      src={normalizeCustomGithubImagePath(customImagePath)}
                      alt={`${customCardName || customCardKind} preview`}
                      className="h-24 w-24 rounded-lg object-cover border border-gray-700 bg-gray-800"
                    />
                    <div className="min-w-0 text-xs text-gray-400">
                      <p className="break-all text-gray-300">{normalizeCustomGithubImagePath(customImagePath)}</p>
                      <p className="mt-2">
                        Custom Legend/Divinity cards are not linked to any player and can only use Plus, Rare, Epic, or Unique rarities.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Team players list with include/exclude for card generation (By Team mode) */}
          {creationMode === 'team' && selectedLeague && selectedTeam && (
            <div className="border border-gray-700 rounded-lg p-4 bg-gray-900/50">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-300">
                  Player del team – scegli per quali generare le card
                </label>
                <div className="flex items-center gap-3 text-sm">
                  <button
                    type="button"
                    onClick={selectAllTeamPlayers}
                    className="text-indigo-400 hover:text-indigo-300"
                  >
                    Seleziona tutti
                  </button>
                  <span className="text-gray-500">|</span>
                  <button
                    type="button"
                    onClick={deselectAllTeamPlayers}
                    className="text-indigo-400 hover:text-indigo-300"
                  >
                    Deseleziona tutti
                  </button>
                </div>
              </div>
              {teamPlayersLoading ? (
                <div className="flex items-center text-gray-400 py-4">
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  Caricamento player...
                </div>
              ) : teamPlayers.length === 0 ? (
                <p className="text-gray-400 text-sm py-2">Nessun player trovato per questo team.</p>
              ) : (
                <div className="max-h-64 overflow-y-auto space-y-2 pr-2">
                  {teamPlayers.map((player) => (
                    <label
                      key={player.id}
                      className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-gray-800/50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPlayerIdsForBulk.has(player.id)}
                        onChange={() => togglePlayerForBulk(player.id)}
                        className="w-5 h-5 text-indigo-600 bg-gray-900 border-gray-600 rounded focus:ring-indigo-500"
                      />
                      <span className="text-white font-medium">{player.fullName}</span>
                      <span className="text-gray-400 text-sm">
                        {player.position} {player.nationality ? `• ${player.nationality}` : ''}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {teamPlayers.length > 0 && (
                <p className="mt-2 text-xs text-gray-400">
                  {selectedPlayerIdsForBulk.size} di {teamPlayers.length} player selezionati per la generazione card
                </p>
              )}
            </div>
          )}

          {/* Player Selection */}
          {creationMode === 'player' && (
            <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Player <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <div className="flex items-center">
                <Search className="absolute left-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={playerSearch}
                  onChange={(e) => {
                    setPlayerSearch(e.target.value);
                    if (e.target.value === '') {
                      setSelectedPlayer(null);
                    }
                  }}
                  placeholder="Search for a player (min 2 characters)..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {selectedPlayer && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedPlayer(null);
                      setPlayerSearch('');
                    }}
                    className="ml-2 p-2 text-gray-400 hover:text-white"
                    title="Clear selection"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              
              {showPlayerDropdown && players.length > 0 && !selectedPlayer && (
                <div className="absolute z-10 w-full mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {players.map((player) => (
                    <button
                      key={player.id}
                      type="button"
                      onClick={() => handlePlayerSelect(player)}
                      className="w-full text-left px-4 py-3 hover:bg-gray-800 border-b border-gray-700 last:border-b-0"
                    >
                      <div className="font-medium text-white">{player.fullName}</div>
                      <div className="text-sm text-gray-400">
                        {player.position} • {player.team || 'N/A'} • {player.nationality || 'N/A'}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Rarity Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Rarity <span className="text-red-400">*</span>
            </label>
            <select
              value={selectedRarity}
              onChange={(e) => setSelectedRarity(e.target.value)}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            >
              <option value="">Select a rarity...</option>
              {visibleRarities.map((rarity) => (
                <option key={rarity.id} value={rarity.id}>
                  {rarity.displayName} {rarity.maxCopiesPerPlayer ? `(Max: ${rarity.maxCopiesPerPlayer})` : '(Unlimited)'}
                </option>
              ))}
            </select>
            {selectedRarityData && selectedRarityData.maxCopiesPerPlayer && (
              <p className="mt-1 text-xs text-gray-400">
                Maximum {selectedRarityData.maxCopiesPerPlayer} copies allowed for this rarity
              </p>
            )}
            {(creationMode === 'player' && selectedPlayer && selectedRarity) && (
              <div className="mt-2 p-3 bg-gray-900/50 rounded-lg border border-gray-700 text-sm">
                {remainingLoading && (
                  <div className="flex items-center text-gray-300">
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Calculating remaining copies...
                  </div>
                )}
                {!remainingLoading && remainingError && (
                  <div className="text-red-300">{remainingError}</div>
                )}
                {!remainingLoading && !remainingError && remainingInfo && (
                  <div className="text-gray-300">
                    <div>
                      Existing copies: <span className="text-white font-medium">{remainingInfo.existingCopies}</span>
                    </div>
                    <div>
                      Max per player: <span className="text-white font-medium">
                        {remainingInfo.maxCopiesPerPlayer === null ? 'Unlimited' : remainingInfo.maxCopiesPerPlayer}
                      </span>
                    </div>
                    <div>
                      Remaining creatable: <span className="text-white font-medium">
                        {remainingInfo.remainingCopies === null ? 'Unlimited' : remainingInfo.remainingCopies}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Total Copies */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Available Copies <span className="text-red-400">*</span>
            </label>
            <input
              type="number"
              value={availableCopies}
              onChange={(e) => setAvailableCopies(e.target.value)}
              min="1"
              max={remainingMax}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter number of copies to create"
              required
            />
            <p className="mt-1 text-xs text-gray-400">
              Copies created with owner_id = NULL (available for marketplace)
              {remainingMax !== undefined ? ` (max ${remainingMax})` : ''}
            </p>
          </div>

          {/* Marketplace Options Section */}
          <div className="border-t border-gray-700 pt-6">
            <h3 className="text-lg font-semibold text-gray-300 mb-4">Marketplace Options (Optional)</h3>
            <p className="text-sm text-gray-400 mb-4">Select one option to create marketplace listings for available copies</p>
            
            {/* Create Sale Option */}
            <div className="mb-4">
              <div className="flex items-center space-x-3 mb-3">
                <input
                  type="checkbox"
                  id="createSale"
                  checked={createSale}
                  onChange={(e) => {
                    setCreateSale(e.target.checked);
                    if (e.target.checked) {
                      setCreateAuction(false);
                      setCreateLoan(false);
                    }
                  }}
                  className="w-5 h-5 text-indigo-600 bg-gray-900 border-gray-700 rounded focus:ring-indigo-500"
                />
                <label htmlFor="createSale" className="text-sm font-medium text-gray-300">
                  Create marketplace sales
                </label>
              </div>
              {createSale && (
                <div className="ml-8 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Price (DIOS) <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      min="0"
                      step="0.01"
                      className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter price in DIOS"
                      required={createSale}
                    />
                    <p className="mt-1 text-xs text-gray-400">
                      Price per card. A 10% fee will be automatically calculated.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Create Auction Option */}
            <div className="mb-4">
              <div className="flex items-center space-x-3 mb-3">
                <input
                  type="checkbox"
                  id="createAuction"
                  checked={createAuction}
                  onChange={(e) => {
                    setCreateAuction(e.target.checked);
                    if (e.target.checked) {
                      setCreateSale(false);
                      setCreateLoan(false);
                    }
                  }}
                  className="w-5 h-5 text-indigo-600 bg-gray-900 border-gray-700 rounded focus:ring-indigo-500"
                />
                <label htmlFor="createAuction" className="text-sm font-medium text-gray-300">
                  Create marketplace auctions
                </label>
              </div>
              {createAuction && (
                <div className="ml-8 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Starting Price (DIOS) <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="number"
                      value={auctionStartingPrice}
                      onChange={(e) => setAuctionStartingPrice(e.target.value)}
                      min="0"
                      step="0.01"
                      className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter starting price"
                      required={createAuction}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Min Increment (%)
                      </label>
                      <input
                        type="number"
                        value={auctionMinIncrementPercent}
                        onChange={(e) => setAuctionMinIncrementPercent(e.target.value)}
                        min="1"
                        max="100"
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <p className="mt-1 text-xs text-gray-400">Default: 5%</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Time Extension (seconds)
                      </label>
                      <input
                        type="number"
                        value={auctionTimeExtensionSeconds}
                        onChange={(e) => setAuctionTimeExtensionSeconds(e.target.value)}
                        min="10"
                        max="300"
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <p className="mt-1 text-xs text-gray-400">Default: 30s</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Duration (hours)
                      </label>
                      <input
                        type="number"
                        value={auctionDurationHours}
                        onChange={(e) => setAuctionDurationHours(e.target.value)}
                        min="0"
                        max="168"
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <p className="mt-1 text-xs text-gray-400">Default: 24 hours</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Duration (minutes)
                      </label>
                      <input
                        type="number"
                        value={auctionDurationMinutes}
                        onChange={(e) => setAuctionDurationMinutes(e.target.value)}
                        min="0"
                        max="59"
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <p className="mt-1 text-xs text-gray-400">Default: 0 minutes</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Duration (seconds)
                      </label>
                      <input
                        type="number"
                        value={auctionDurationSeconds}
                        onChange={(e) => setAuctionDurationSeconds(e.target.value)}
                        min="0"
                        max="59"
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <p className="mt-1 text-xs text-gray-400">Default: 0 seconds</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Create Loan Option */}
            <div className="mb-4">
              <div className="flex items-center space-x-3 mb-3">
                <input
                  type="checkbox"
                  id="createLoan"
                  checked={createLoan}
                  onChange={(e) => {
                    setCreateLoan(e.target.checked);
                    if (e.target.checked) {
                      setCreateSale(false);
                      setCreateAuction(false);
                    }
                  }}
                  className="w-5 h-5 text-indigo-600 bg-gray-900 border-gray-700 rounded focus:ring-indigo-500"
                />
                <label htmlFor="createLoan" className="text-sm font-medium text-gray-300">
                  Create marketplace loans
                </label>
              </div>
              {createLoan && (
                <div className="ml-8 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Price per Gameweek (DIOS) <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="number"
                      value={loanPricePerGW}
                      onChange={(e) => setLoanPricePerGW(e.target.value)}
                      min="0"
                      step="0.01"
                      className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter price per gameweek"
                      required={createLoan}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Duration (Gameweeks) <span className="text-red-400">*</span>
                    </label>
                    <select
                      value={loanDurationGW}
                      onChange={(e) => setLoanDurationGW(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required={createLoan}
                    >
                      <option value="1">1 Gameweek</option>
                      <option value="3">3 Gameweeks</option>
                      <option value="5">5 Gameweeks</option>
                      <option value="10">10 Gameweeks</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-400">
                      Total price: {loanPricePerGW && loanDurationGW ? (parseFloat(loanPricePerGW) * parseInt(loanDurationGW)).toFixed(2) : '0.00'} DIOS (10% fee included)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={resetForm}
              className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              disabled={loading}
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5 mr-2" />
                  Create Card
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminCards;
